import { computed, ref } from 'vue'
import type { LocationQueryRaw, RouteLocationNormalizedLoaded } from 'vue-router'
import { useRoute, useRouter } from 'vue-router'
import { apiFetch } from '../../services/api'

/**
 * The candidate list query: URL <-> filters, the fetch, paging and ordering.
 *
 * Shared by the Candidates page (one play) and the Engaged page (every play),
 * which are the same rows at the same grain — `sales_play_contacts` /
 * `sales_play_accounts` memberships — differing only in what they pin. Sharing
 * the parser and the fetch is what keeps the two from disagreeing about sort
 * order, which would make the same candidate appear in a different position
 * depending on which page you came from.
 *
 * Modeled on `useActionsQuery` deliberately: same URL-as-state contract, same
 * page-crossing j/k traversal, same module-scope cache.
 */

export const CANDIDATES_PAGE_SIZE = 50

/**
 * Ceiling on "select all matching". Bulk actions post every id in one request
 * body, so this bounds both the fan-out fetch and that payload.
 */
export const SELECT_ALL_MAX = 2000

export type CandidateFilters = {
  /** Membership state. 'all' means every state. */
  stage: string
  /** Empty = every play (the Engaged page's cross-play mode). */
  play: string
  owner: string
  accountId: string
  kind: string
  /** Account-only. Empty = no cadence filter. Forces kind='account' when set. */
  prospectingCadence: string
  q: string
  sort: string
  page: number
}

export const SORT_OPTIONS = [
  { value: 'stage_changed_desc', label: 'Recently changed' },
  { value: 'stage_changed_asc', label: 'Oldest change' },
  { value: 'engagement_desc', label: 'Strongest engagement' },
  { value: 'name_asc', label: 'Name A–Z' },
  { value: 'name_desc', label: 'Name Z–A' },
  { value: 'added_desc', label: 'Newest added' },
  { value: 'added_asc', label: 'Oldest added' },
  { value: 'account_asc', label: 'Grouped by account' },
]

const VALID_SORTS = new Set(SORT_OPTIONS.map((o) => o.value))

export const STAGE_OPTIONS = [
  { value: 'all', label: 'All stages' },
  { value: 'staged', label: 'Staged' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'active', label: 'Active' },
  { value: 'engaged', label: 'Engaged' },
  { value: 'timed_out', label: 'Timed Out' },
  { value: 'disqualified', label: 'Disqualified' },
]

const VALID_STAGES = new Set(STAGE_OPTIONS.map((o) => o.value))

export const KIND_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'account', label: 'Companies' },
  { value: 'contact', label: 'People' },
]

export const PROSPECTING_CADENCE_OPTIONS = [
  { value: '', label: 'Any cadence' },
  { value: 'active', label: 'Active' },
  { value: 'opportunistic', label: 'Opportunistic' },
  { value: 'timed_followup', label: 'Timed follow-up' },
  { value: 'off', label: 'Off' },
]

const VALID_PROSPECTING_CADENCES = new Set(
  PROSPECTING_CADENCE_OPTIONS.map((o) => o.value).filter(Boolean),
)

export type CandidateQueryOptions = {
  /** Route name to push filter changes to. */
  routeName: string
  /** Extra route params to preserve on every push (e.g. the play id). */
  routeParams?: () => Record<string, string>
  /** Pinned play id — the Candidates page sets this; Engaged leaves it empty. */
  playId?: () => string
  play?: () => any
  /** Stage the page pins and the user cannot change (Engaged pins 'engaged'). */
  fixedStage?: string
  /** Kind the page pins and the user cannot change (Engaged pins 'contact'). */
  fixedKind?: string
  /** Default stage when the URL says nothing. */
  defaultStage?: string
  /** Default sort when the URL says nothing. */
  defaultSort?: string
  /** Ask the server for each row's touch history (reachouts, replies, calls,
   *  meetings, opportunities). Costs extra queries — opt in per page. */
  includeActivity?: boolean
  includeAccountContacts?: boolean
  includeSequenceStatus?: boolean | (() => boolean)
  includeDialerStatus?: boolean | (() => boolean)
}

export function parseFilters(
  route: RouteLocationNormalizedLoaded,
  options: CandidateQueryOptions,
): CandidateFilters {
  const str = (key: string, fallback = '') => {
    const raw = route.query[key]
    const value = Array.isArray(raw) ? raw[0] : raw
    return typeof value === 'string' && value.trim() ? value.trim() : fallback
  }
  const page = Number.parseInt(str('p', '1'), 10)
  const defaultStage = options.defaultStage || 'all'
  const rawStage = options.fixedStage || str('stage', defaultStage)
  const rawSort = str('sort', options.defaultSort || 'stage_changed_desc')
  const rawCadence = str('cadence')
  // A cadence filter only ever matches account rows — pin kind to 'account'
  // whenever one is active rather than silently returning an empty list.
  const cadence = VALID_PROSPECTING_CADENCES.has(rawCadence) ? rawCadence : ''
  return {
    // Anything unrecognized falls back to the default rather than being passed
    // through — the server would reject it, and a silently unfiltered list is
    // worse than an honest default.
    stage: VALID_STAGES.has(rawStage) ? rawStage : defaultStage,
    play: options.playId ? options.playId() : str('play'),
    owner: str('owner'),
    accountId: str('account_id'),
    kind: options.fixedKind || (cadence ? 'account' : str('kind', 'all')),
    prospectingCadence: cadence,
    q: str('q'),
    sort: VALID_SORTS.has(rawSort) ? rawSort : (options.defaultSort || 'stage_changed_desc'),
    page: Number.isFinite(page) && page > 0 ? page : 1,
  }
}

/**
 * Filters -> query params, omitting anything at its default so the canonical
 * URL stays clean.
 */
export function filtersToQuery(
  filters: CandidateFilters,
  options: CandidateQueryOptions,
): LocationQueryRaw {
  const query: LocationQueryRaw = {}
  const defaultStage = options.defaultStage || 'all'
  const defaultSort = options.defaultSort || 'stage_changed_desc'
  if (!options.fixedStage && filters.stage !== defaultStage) query.stage = filters.stage
  // A pinned play lives in the route params, not the query string.
  if (!options.playId && filters.play) query.play = filters.play
  if (filters.owner) query.owner = filters.owner
  if (filters.accountId) query.account_id = filters.accountId
  if (!options.fixedKind && filters.kind && filters.kind !== 'all') query.kind = filters.kind
  if (filters.prospectingCadence) query.cadence = filters.prospectingCadence
  if (filters.q) query.q = filters.q
  if (filters.sort !== defaultSort) query.sort = filters.sort
  if (filters.page > 1) query.p = String(filters.page)
  return query
}

function buildParams(filters: CandidateFilters, options: CandidateQueryOptions): URLSearchParams {
  const params = new URLSearchParams()
  params.set('state', filters.stage)
  params.set('kind', filters.kind || 'all')
  params.set('sort_order', filters.sort)
  if (filters.play) params.set('sales_play_id', filters.play)
  if (filters.owner) params.set('owner_user_id', filters.owner)
  if (filters.accountId) params.set('account_id', filters.accountId)
  if (filters.prospectingCadence) params.set('prospecting_cadence', filters.prospectingCadence)
  if (filters.q) params.set('q', filters.q)
  if (options.includeActivity) params.set('include_activity', 'true')
  if (options.includeAccountContacts) params.set('include_account_contacts', 'true')
  if (resolveFlag(options.includeSequenceStatus)) params.set('include_sequence_status', 'true')
  if (resolveFlag(options.includeDialerStatus)) params.set('include_dialer_status', 'true')
  return params
}

function resolveFlag(flag: boolean | (() => boolean) | undefined): boolean {
  return typeof flag === 'function' ? flag() : !!flag
}

export function useCandidateQuery(options: CandidateQueryOptions) {
  const route = useRoute()
  const router = useRouter()

  const items = ref<any[]>([])
  const total = ref(0)
  const loading = ref(false)
  const error = ref('')

  const filters = computed(() => parseFilters(route, options))
  const pageCount = computed(() => Math.max(1, Math.ceil(total.value / CANDIDATES_PAGE_SIZE)))

  const rangeStart = computed(() =>
    total.value === 0 ? 0 : (filters.value.page - 1) * CANDIDATES_PAGE_SIZE + 1)
  const rangeEnd = computed(() =>
    Math.min(filters.value.page * CANDIDATES_PAGE_SIZE, total.value))
  const rangeLabel = computed(() =>
    total.value === 0 ? 'No candidates' : `${rangeStart.value}–${rangeEnd.value} of ${total.value}`)

  /** Merge preserving object identity, so an open drawer doesn't flicker on poll. */
  function mergeItems(incoming: any[]) {
    const existing = new Map(items.value.map((item) => [String(item.membership_id), item]))
    items.value = incoming.map((next) => {
      const prev = existing.get(String(next.membership_id))
      if (!prev) return next
      return JSON.stringify(prev) === JSON.stringify(next) ? prev : next
    })
  }

  async function load({ forPoll = false } = {}) {
    if (!forPoll) loading.value = true
    error.value = ''
    const params = buildParams(filters.value, options)
    params.set('limit', String(CANDIDATES_PAGE_SIZE))
    params.set('offset', String((filters.value.page - 1) * CANDIDATES_PAGE_SIZE))
    try {
      const data = await apiFetch(`/candidates?${params.toString()}`)
      const incoming = Array.isArray(data?.members) ? data.members : []
      if (forPoll) mergeItems(incoming)
      else items.value = incoming
      total.value = Number(data?.total) || 0
    } catch (e: any) {
      // A failed poll must not blank a list the user is working in.
      if (!forPoll) {
        error.value = e?.message || 'Failed to load candidates.'
        items.value = []
        total.value = 0
      }
    } finally {
      loading.value = false
    }
  }

  async function exportCsv() {
    error.value = ''
    const params = buildParams(filters.value, options)
    try {
      const blob = await apiFetch(`/candidates/export_csv?${params.toString()}`, {
        responseType: 'blob',
      })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      const slug = String(options.play?.()?.name || 'salesplay').trim().toLowerCase()
        .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
      if (options.fixedKind == 'account'){
         a.download = `${slug}_companies.csv`
      } else {
         a.download = `${slug}_people.csv`
      }
      
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
    } catch (e: any) {
      error.value = e?.message || 'CSV Export Failed'
    }
  }


  /**
   * Every row matching the current filters, across all pages — for
   * "select all N matching".
   *
   * Returns rows rather than ids because callers need per-row state to decide
   * which bulk actions apply. Capped at SELECT_ALL_MAX so a 50k-row filter
   * can't hang the tab or build a request body the API will reject.
   */
  async function fetchAllMatching(): Promise<any[]> {
    // No activity here — selection only needs ids and per-row state, and this
    // fans out to SELECT_ALL_MAX rows.
    const params = buildParams(filters.value, { ...options, includeActivity: false })
    const pageSize = 200
    const out: any[] = []
    for (let offset = 0; offset < SELECT_ALL_MAX; offset += pageSize) {
      params.set('limit', String(Math.min(pageSize, SELECT_ALL_MAX - offset)))
      params.set('offset', String(offset))
      const data = await apiFetch(`/candidates?${params.toString()}`)
      const rows = Array.isArray(data?.members) ? data.members : []
      out.push(...rows)
      if (data?.next_offset == null || rows.length === 0) break
    }
    return out
  }

  // ── Navigation ─────────────────────────────────────────────────
  // Filter changes reset paging in the SAME push, so a filter change costs one
  // history entry rather than two.

  function setFilters(patch: Partial<CandidateFilters>) {
    const next = { ...filters.value, ...patch }
    if (!('page' in patch)) next.page = 1
    void router.push({
      name: options.routeName,
      params: options.routeParams ? options.routeParams() : undefined,
      query: filtersToQuery(next, options),
    })
  }

  function goToPage(page: number) {
    const clamped = Math.min(Math.max(1, page), pageCount.value)
    if (clamped === filters.value.page) return
    setFilters({ page: clamped })
  }

  // ── Ordered traversal (arrow keys) ─────────────────────────────
  // Crossing a page boundary rather than stopping at it, so paging and keyboard
  // nav behave as one system.

  function indexOf(id: string) {
    return items.value.findIndex((item) => String(item.membership_id) === String(id))
  }

  async function idAfter(id: string): Promise<string | null> {
    const index = indexOf(id)
    if (index === -1) return null
    if (index + 1 < items.value.length) return String(items.value[index + 1].membership_id)
    if (filters.value.page >= pageCount.value) return null
    setFilters({ page: filters.value.page + 1 })
    await load()
    return items.value.length ? String(items.value[0].membership_id) : null
  }

  async function idBefore(id: string): Promise<string | null> {
    const index = indexOf(id)
    if (index === -1) return null
    if (index > 0) return String(items.value[index - 1].membership_id)
    if (filters.value.page <= 1) return null
    setFilters({ page: filters.value.page - 1 })
    await load()
    return items.value.length ? String(items.value[items.value.length - 1].membership_id) : null
  }

  return {
    filters,
    items,
    total,
    loading,
    error,
    pageCount,
    rangeStart,
    rangeEnd,
    rangeLabel,
    load,
    exportCsv,
    fetchAllMatching,
    setFilters,
    goToPage,
    idAfter,
    idBefore,
  }
}
